// src/stores/PartnersStore.js - Zustand Store replacing PartnerSocketService + PartnersProvider
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import SOCKET_EVENTS from '../services/socket/socketEvents';

// Store creation with selector subscription middleware
const usePartnersStore = create(
  subscribeWithSelector((set, get) => ({
    // ================================
    // STATE (mirrors PartnersProvider exactly)
    // ================================
    
    // Main partner data
    connectedPartners: [],
    pendingConnections: [],
    networkProfile: {},
    networkStats: {},
    
    // Search and discovery
    searchResults: [],
    searchLoading: false,
    
    // Referral collaborations
    referralCollaborations: [],
    selectedCollaboration: null,
    collaborationLoading: false,
    
    // Partner availability
    partnerAvailability: {},
    availabilityLoading: false,
    
    // Partner services
    partnerServices: {}, // Keyed by partner ID
    servicesLoading: false,
    
    // Loading states
    partnersLoading: false,
    connectionLoading: false,
    
    // Connection state
    isConnected: false,
    
    // Cache state (from PartnerSocketService)
    cache: {
      connectedPartners: [],
      pendingConnections: [],
      networkProfile: {},
      networkStats: {},
      searchResults: [],
      referralCollaborations: [],
      selectedCollaboration: null,
      partnerAvailability: {},
      partnerServices: {},
      lastFetch: null
    },
    
    // ================================
    // SOCKET OPERATIONS (from PartnerSocketService)
    // ================================
    
    /**
     * Request complete partner network information - NO RETURN VALUE
     * @param {object} options - Loading options
     */
    loadPartnerNetwork: (options = {}) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ PartnersStore: Socket service not initialized');
        return;
      }
      
      const {
        includePending = true,
        includeStats = true,
        ...restOptions  // FIXED: Capture any additional options
      } = options;

      console.log('🤝 PartnersStore: Loading partner network');
      
      set({ partnersLoading: true });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_PARTNER_NETWORK, {
        include_pending: includePending,
        include_stats: includeStats,
        ...restOptions  // FIXED: Pass through any additional options
      });
    },

    /**
     * Request partner business search - NO RETURN VALUE
     * @param {object} searchParams - Search criteria
     */
    searchPartnerBusinesses: (searchParams = {}) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ PartnersStore: Socket service not initialized');
        return;
      }
      
      const {
        search_term = '',
        business_types = [],
        location_radius = 50,
        include_contact_info = true,
        ...restParams  // FIXED: Capture additional search params
      } = searchParams;

      console.log('🔍 PartnersStore: Searching partner businesses:', searchParams);
      
      set({ searchLoading: true });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.SEARCH_PARTNER_BUSINESSES, {
        search_term,
        business_types,
        location_radius,
        include_contact_info,
        ...restParams  // FIXED: Pass through additional params
      });
    },

    /**
     * Request referral collaborations - NO RETURN VALUE
     * @param {object} options - Loading options
     */
    loadReferralCollaborations: (options = {}) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ PartnersStore: Socket service not initialized');
        return;
      }
      
      const {
        includeCompleted = false,
        limit = 50,
        ...restOptions  // FIXED: Capture additional options
      } = options;

      console.log('💼 PartnersStore: Loading referral collaborations');
      
      set({ collaborationLoading: true });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_REFERRAL_COLLABORATIONS, {
        include_completed: includeCompleted,
        limit: limit,
        ...restOptions  // FIXED: Pass through additional options
      });
    },

    /**
     * Request partner's availability - NO RETURN VALUE
     * @param {string} partnerId - Partner business ID
     * @param {object} dateRange - Date range for availability
     */
    loadPartnerAvailability: (partnerId, dateRange = {}) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ PartnersStore: Socket service not initialized');
        return;
      }
      
      console.log('📅 PartnersStore: Loading partner availability for:', partnerId);
      
      set({ availabilityLoading: true });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_PARTNER_AVAILABILITY, {
        partner_business_id: partnerId,
        start_date: dateRange.start_date,
        end_date: dateRange.end_date,
        ...dateRange  // FIXED: Pass through any additional date range options
      });
    },

    /**
     * Request partner's services - NO RETURN VALUE
     * @param {string} partnerId - Partner business ID
     */
    loadPartnerServices: (partnerId) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ PartnersStore: Socket service not initialized');
        return;
      }
      
      console.log('🔧 PartnersStore: Loading partner services for:', partnerId);
      
      set({ servicesLoading: true });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.GET_PARTNER_SERVICES, {
        partner_business_id: partnerId
      });
    },

    /**
     * Request to update network profile - NO RETURN VALUE
     * @param {object} profileData - Profile information
     */
    updateNetworkProfile: (profileData) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ PartnersStore: Socket service not initialized');
        return;
      }
      
      console.log('💼 PartnersStore: Updating network profile');
      
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.UPDATE_NETWORK_PROFILE, profileData);
    },

    /**
     * Request to send connection request - NO RETURN VALUE
     * @param {object} requestData - Connection request data
     */
    sendConnectionRequest: (requestData) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ PartnersStore: Socket service not initialized');
        return;
      }
      
      console.log('📤 PartnersStore: Sending connection request');
      
      set({ connectionLoading: true });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.SEND_CONNECTION_REQUEST, {
        target_business_id: requestData.target_business_id,
        notes: requestData.connection_note || ''
      });
    },

    /**
     * Request to respond to connection request - NO RETURN VALUE
     * @param {object} responseData - Connection response data
     */
    respondToConnectionRequest: (responseData) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ PartnersStore: Socket service not initialized');
        return;
      }
      
      console.log('📥 PartnersStore: Responding to connection request');
      
      set({ connectionLoading: true });
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.RESPOND_TO_CONNECTION_REQUEST, {
        request_id: responseData.request_id,
        response: responseData.response,
        notes: responseData.response_note || ''
      });
    },

    /**
     * Request to add referral note - NO RETURN VALUE
     * @param {string} collaborationId - Collaboration ID
     * @param {string} noteContent - Note content
     * @param {boolean} isPrivate - Whether note is private
     */
    addReferralNote: (collaborationId, noteContent, isPrivate = false) => {
      const { socketManager } = get()._getSocketManager();
      if (!socketManager?.universalHandler) {
        console.error('❌ PartnersStore: Socket service not initialized');
        return;
      }
      
      console.log('📝 PartnersStore: Adding referral note');
      
      
      socketManager.universalHandler.emit(SOCKET_EVENTS.ADD_REFERRAL_NOTE, {
        collaboration_id: collaborationId,
        note_content: noteContent,
        is_private: isPrivate
      });
    },
    
    // ================================
    // UTILITY FUNCTIONS (from PartnersProvider)
    // ================================
    
    refreshPartnerNetwork: (options = {}) => {
      set(state => ({
        cache: {
          connectedPartners: [],
          pendingConnections: [],
          networkProfile: {},
          networkStats: {},
          searchResults: [],
          referralCollaborations: [],
          selectedCollaboration: null,
          partnerAvailability: {},
          partnerServices: {},
          lastFetch: null
        }
      }));
      
      get().loadPartnerNetwork({ ...options, forceRefresh: true });
    },
    
    clearSearchResults: () => {
      set({ searchResults: [] });
    },
    
    selectCollaboration: (collaboration) => {
      set({ selectedCollaboration: collaboration });
    },
    
    clearSelectedCollaboration: () => {
      set({ selectedCollaboration: null });
    },

    getPartnerById: (partnerId) => {
      const { connectedPartners } = get();
      return connectedPartners.find(p => p.id === partnerId) || null;
    },
    
    getCollaborationById: (collaborationId) => {
      const { referralCollaborations } = get();
      return referralCollaborations.find(c => c.id === collaborationId) || null;
    },
    
    getIncomingRequests: () => {
      const { pendingConnections } = get();
      if (Array.isArray(pendingConnections)) {
        return pendingConnections.filter(req => req.type === 'received');
      } else {
        return pendingConnections.received || [];
      }
    },
    
    getOutgoingRequests: () => {
      const { pendingConnections } = get();
      if (Array.isArray(pendingConnections)) {
        return pendingConnections.filter(req => req.type === 'sent');
      } else {
        return pendingConnections.sent || [];
      }
    },
    
    getActiveCollaborations: () => {
      const { referralCollaborations } = get();
      return referralCollaborations.filter(collab => collab.status === 'active');
    },
    
    // ================================
    // INTERNAL STATE UPDATERS (called by socket listeners)
    // ================================
    
    _setPartnerNetworkLoaded: (data) => {
      console.log('📡 PartnersStore: Partner network loaded');
      
      set(state => ({
        connectedPartners: data.connected_partners || [],
        pendingConnections: data.pending_connections || [],
        networkProfile: data.network_profile || {},
        networkStats: data.network_stats || {},
        partnersLoading: false,
        cache: {
          ...state.cache,
          connectedPartners: data.connected_partners || [],
          pendingConnections: data.pending_connections || [],
          networkProfile: data.network_profile || {},
          networkStats: data.network_stats || {},
          lastFetch: new Date()
        }
      }));
    },
    
    _setPartnerSearchResults: (data) => {
      console.log('📡 PartnersStore: Search results received');
      
      set(state => ({
        searchResults: data.search_results || [],
        searchLoading: false,
        cache: {
          ...state.cache,
          searchResults: data.search_results || []
        }
      }));
    },
    
    _setConnectionRequestSent: (data) => {
      console.log('📡 PartnersStore: Connection request sent');
      
      set(state => {
        const newRequest = {
          id: data.request_id,
          business_name: data.target_business.name,
          business_type: data.target_business.business_type,
          status: 'pending',
          type: 'sent',
          note: data.message,
          created_at: new Date().toISOString()
        };
        
        let updatedPendingConnections;
        if (Array.isArray(state.pendingConnections)) {
          updatedPendingConnections = [...state.pendingConnections, newRequest];
        } else {
          updatedPendingConnections = {
            ...state.pendingConnections,
            sent: [...(state.pendingConnections.sent || []), newRequest]
          };
        }
        
        return {
          pendingConnections: updatedPendingConnections,
          connectionLoading: false,
            cache: {
            ...state.cache,
            pendingConnections: updatedPendingConnections
          }
        };
      });
    },
    
    _setConnectionRequestReceived: (data) => {
      console.log('📡 PartnersStore: Connection request received');
      
      set(state => {
        const newRequest = {
          id: data.request_id,
          business_name: data.requesting_business.name,
          business_type: data.requesting_business.business_type,
          status: 'pending',
          type: 'received',
          note: data.message,
          created_at: data.created_at
        };
        
        let updatedPendingConnections;
        if (Array.isArray(state.pendingConnections)) {
          updatedPendingConnections = [...state.pendingConnections, newRequest];
        } else {
          updatedPendingConnections = {
            ...state.pendingConnections,
            received: [...(state.pendingConnections.received || []), newRequest]
          };
        }
        
        return {
          pendingConnections: updatedPendingConnections,
          cache: {
            ...state.cache,
            pendingConnections: updatedPendingConnections
          }
        };
      });
    },
    
    _setConnectionRequestResponse: (data) => {
      console.log('📡 PartnersStore: Connection request response:', data.response);
      
      set(state => {
        let updatedConnectedPartners = state.connectedPartners;
        let updatedPendingConnections = state.pendingConnections;
        
        if (data.response === 'accepted') {
          const newPartner = {
            id: data.partner_data?.id || `partner_${Date.now()}`,
            business_name: data.partner_data?.business_name,
            business_type: data.partner_data?.business_type,
            status: 'accepted',
            connected_at: new Date().toISOString()
          };
          
          updatedConnectedPartners = [...state.connectedPartners, newPartner];
        }
        
        // Remove from pending connections
        if (Array.isArray(state.pendingConnections)) {
          updatedPendingConnections = state.pendingConnections.filter(req => req.id !== data.request_id);
        } else {
          updatedPendingConnections = {
            sent: (state.pendingConnections.sent || []).filter(req => req.id !== data.request_id),
            received: (state.pendingConnections.received || []).filter(req => req.id !== data.request_id)
          };
        }
        
        return {
          connectedPartners: updatedConnectedPartners,
          pendingConnections: updatedPendingConnections,
          connectionLoading: false,
            cache: {
            ...state.cache,
            connectedPartners: updatedConnectedPartners,
            pendingConnections: updatedPendingConnections
          }
        };
      });
    },
    
    _setNetworkProfileUpdated: (data) => {
      console.log('📡 PartnersStore: Network profile updated');
      
      set(state => {
        const updatedProfile = { ...state.networkProfile, ...data };
        
        return {
          networkProfile: updatedProfile,
            cache: {
            ...state.cache,
            networkProfile: updatedProfile
          }
        };
      });
    },
    
    _setReferralCollaborationsLoaded: (data) => {
      console.log('📡 PartnersStore: Referral collaborations loaded');
      
      set(state => ({
        referralCollaborations: data.collaborations || [],
        collaborationLoading: false,
        cache: {
          ...state.cache,
          referralCollaborations: data.collaborations || []
        }
      }));
    },
    
    _setReferralNoteAdded: (data) => {
      console.log('📡 PartnersStore: Referral note added');
      
      set(state => {
        const updatedCollaborations = state.referralCollaborations.map(collab => 
          collab.id === data.collaboration_id 
            ? { ...collab, notes: [...(collab.notes || []), data.note] }
            : collab
        );
        
        const updatedSelectedCollaboration = state.selectedCollaboration?.id === data.collaboration_id 
          ? { ...state.selectedCollaboration, notes: [...(state.selectedCollaboration.notes || []), data.note] }
          : state.selectedCollaboration;
        
        return {
          referralCollaborations: updatedCollaborations,
          selectedCollaboration: updatedSelectedCollaboration,
            cache: {
            ...state.cache,
            referralCollaborations: updatedCollaborations,
            selectedCollaboration: updatedSelectedCollaboration
          }
        };
      });
    },
    
    _setPartnerAvailabilityLoaded: (data) => {
      console.log('📡 PartnersStore: Partner availability loaded');
      
      set(state => {
        const { partner_business_id, ...availabilityData } = data;
        const updatedAvailability = {
          ...state.partnerAvailability,
          [partner_business_id]: availabilityData
        };
        
        return {
          partnerAvailability: updatedAvailability,
          availabilityLoading: false,
            cache: {
            ...state.cache,
            partnerAvailability: updatedAvailability
          }
        };
      });
    },
    
    _setPartnerServicesLoaded: (data) => {
      console.log('🔧 PartnersStore: Partner services loaded:', data.services.length);
      
      set(state => {
        const updatedServices = {
          ...state.partnerServices,
          [data.partner_business_id]: data.services
        };
        
        return {
          partnerServices: updatedServices,
          servicesLoading: false,
          cache: {
            ...state.cache,
            partnerServices: updatedServices
          }
        };
      });
    },

    // ================================
    // SOCKET CONNECTION MANAGEMENT
    // ================================
    
    _setConnectionState: (isConnected) => {
      set({ isConnected });
    },
    
    _getSocketManager: () => {
      if (typeof window !== 'undefined' && window.__socketManager) {
        return { socketManager: window.__socketManager };
      }
      return { socketManager: null };
    },
    
    _handleAuthReset: () => {
      console.log('🧹 PartnersStore: Resetting all state due to auth reset');
      
      set({
        connectedPartners: [],
        pendingConnections: [],
        networkProfile: {},
        networkStats: {},
        searchResults: [],
        searchLoading: false,
        referralCollaborations: [],
        selectedCollaboration: null,
        collaborationLoading: false,
        partnerAvailability: {},
        availabilityLoading: false,
        partnerServices: {},
        servicesLoading: false,
        partnersLoading: false,
        connectionLoading: false,
        isConnected: false,
        cache: {
          connectedPartners: [],
          pendingConnections: [],
          networkProfile: {},
          networkStats: {},
          searchResults: [],
          referralCollaborations: [],
          selectedCollaboration: null,
          partnerAvailability: {},
          partnerServices: {},
          lastFetch: null
        },
        _initialized: false
      });
    },
    
    // ================================
    // INITIALIZATION AND AUTO-LOAD
    // ================================
    
    _initialized: false,
    
    _initialize: (socketManager) => {
      const state = get();
      
      console.log('🤝 PartnersStore: Initializing with socket manager');
      
      if (typeof window !== 'undefined') {
        window.__socketManager = socketManager;
      }
      
      set({ 
        _initialized: true,
        isConnected: socketManager?.isAuthenticated || false 
      });
      
      if (socketManager?.isAuthenticated) {
        console.log('🔄 PartnersStore: Auto-loading partner network (first time)');
        state.loadPartnerNetwork({
          include_pending: true,
          include_stats: true
        });
      }
    }
  }))
);

// ================================
// SOCKET EVENT LISTENER SETUP (runs once when module loads)
// ================================


export const initializePartnersStoreSocketListeners = (socketManager) => {
  if (!socketManager?.universalHandler) {
    return;
  }
  
  console.log('👂 PartnersStore: Setting up socket event listeners');
  
  const { universalHandler } = socketManager;
  
  const handleSocketResponse = (responseData) => {
    const eventName = responseData.event;
    const store = usePartnersStore.getState();
    
    switch (eventName) {
      case SOCKET_EVENTS.PARTNER_NETWORK_LOADED:
        store._setPartnerNetworkLoaded(responseData.data);
        break;
      case SOCKET_EVENTS.PARTNER_SEARCH_RESULTS:
        store._setPartnerSearchResults(responseData.data);
        break;
      case SOCKET_EVENTS.CONNECTION_REQUEST_SENT:
        store._setConnectionRequestSent(responseData.data);
        break;
      case SOCKET_EVENTS.CONNECTION_REQUEST_RECEIVED:
        store._setConnectionRequestReceived(responseData.data);
        break;
      case SOCKET_EVENTS.CONNECTION_REQUEST_RESPONSE:
        store._setConnectionRequestResponse(responseData.data);
        break;
      case SOCKET_EVENTS.NETWORK_PROFILE_UPDATED:
        store._setNetworkProfileUpdated(responseData.data);
        break;
      case SOCKET_EVENTS.REFERRAL_COLLABORATIONS_LOADED:
        store._setReferralCollaborationsLoaded(responseData.data);
        break;
      case SOCKET_EVENTS.REFERRAL_NOTE_ADDED:
        store._setReferralNoteAdded(responseData.data);
        break;
      case SOCKET_EVENTS.PARTNER_AVAILABILITY_LOADED:
        store._setPartnerAvailabilityLoaded(responseData.data);
        break;
      case SOCKET_EVENTS.PARTNER_SERVICES_LOADED:
        store._setPartnerServicesLoaded(responseData.data);
        break;
      // ❌ ERROR EVENTS - NotificationListener handles display
      case SOCKET_EVENTS.GET_PARTNER_NETWORK_FAILED:
        set({ partnersLoading: false });
        break;
      case SOCKET_EVENTS.SEARCH_PARTNER_BUSINESSES_FAILED:
        set({ searchLoading: false });
        break;
      case SOCKET_EVENTS.SEND_CONNECTION_REQUEST_FAILED:
      case SOCKET_EVENTS.RESPOND_TO_CONNECTION_REQUEST_FAILED:
        set({ connectionLoading: false });
        break;
      case SOCKET_EVENTS.UPDATE_NETWORK_PROFILE_FAILED:
        console.log('❌ PartnersStore: Operation failed -', responseData.message);
        break;
      case SOCKET_EVENTS.GET_REFERRAL_COLLABORATIONS_FAILED:
        set({ collaborationLoading: false });
        break;
      case SOCKET_EVENTS.GET_PARTNER_AVAILABILITY_FAILED:
        set({ availabilityLoading: false });
        break;
      case SOCKET_EVENTS.GET_PARTNER_SERVICES_FAILED:
        set({ servicesLoading: false });
        break;
      default:
        break;
    }
  };
  
  const partnerEvents = [
    SOCKET_EVENTS.PARTNER_NETWORK_LOADED,
    SOCKET_EVENTS.PARTNER_SEARCH_RESULTS,
    SOCKET_EVENTS.CONNECTION_REQUEST_SENT,
    SOCKET_EVENTS.CONNECTION_REQUEST_RECEIVED,
    SOCKET_EVENTS.CONNECTION_REQUEST_RESPONSE,
    SOCKET_EVENTS.NETWORK_PROFILE_UPDATED,
    SOCKET_EVENTS.REFERRAL_COLLABORATIONS_LOADED,
    SOCKET_EVENTS.REFERRAL_NOTE_ADDED,
    SOCKET_EVENTS.PARTNER_AVAILABILITY_LOADED,
    SOCKET_EVENTS.PARTNER_SERVICES_LOADED,
    SOCKET_EVENTS.GET_PARTNER_NETWORK_FAILED,
    SOCKET_EVENTS.SEARCH_PARTNER_BUSINESSES_FAILED,
    SOCKET_EVENTS.SEND_CONNECTION_REQUEST_FAILED,
    SOCKET_EVENTS.RESPOND_TO_CONNECTION_REQUEST_FAILED,
    SOCKET_EVENTS.UPDATE_NETWORK_PROFILE_FAILED,
    SOCKET_EVENTS.GET_REFERRAL_COLLABORATIONS_FAILED,
    SOCKET_EVENTS.GET_PARTNER_AVAILABILITY_FAILED,
    SOCKET_EVENTS.GET_PARTNER_SERVICES_FAILED
  ];
  
  partnerEvents.forEach(eventName => {
    universalHandler.on(eventName, (data) => {
      handleSocketResponse({ event: eventName, data });
    });
  });
  
  if (typeof window !== 'undefined') {
    window.addEventListener('auth-state-reset', () => {
      usePartnersStore.getState()._handleAuthReset();
    });
  }

  usePartnersStore.getState()._initialize(socketManager);
  
  console.log('✅ PartnersStore: Socket event listeners initialized');
};

export default usePartnersStore;